// Simple consent manager for analytics (no cookies required)
// - Respects browser Do Not Track (DNT)
// - Persists user choice to localStorage

export type ConsentState = 'unknown' | 'granted' | 'denied';

const STORAGE_KEY = 'analytics_consent';

export function getDoNotTrackEnabled(): boolean {
  // Different browsers expose DNT differently
  const dnt = (navigator as any).doNotTrack || (window as any).doNotTrack || (navigator as any).msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
}

export function getStoredConsent(): ConsentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'granted' || raw === 'denied') return raw;
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

export function setStoredConsent(state: Exclude<ConsentState, 'unknown'>) {
  try {
    localStorage.setItem(STORAGE_KEY, state);
    // broadcast to listeners (e.g., other tabs/components)
    window.dispatchEvent(new CustomEvent('analytics-consent-changed', { detail: state }));
  } catch {
    // ignore
  }
}

export function getEffectiveConsent(): ConsentState {
  if (getDoNotTrackEnabled() && process.env.REACT_APP_DNT_RESPECT !== 'false') {
    return 'denied';
  }
  return getStoredConsent();
}

export function onConsentChange(listener: (state: ConsentState) => void): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail as ConsentState | undefined;
    listener(detail ?? getEffectiveConsent());
  };
  window.addEventListener('analytics-consent-changed', handler as EventListener);
  return () => window.removeEventListener('analytics-consent-changed', handler as EventListener);
}
